import React, { useEffect, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { createSetupClient } from '../../api/setupClient'
import { SetupWizard } from './SetupWizard'

type SetupDestination = 'loading' | 'wizard' | 'admin'

/**
 * Selects the supported first-run entry point without creating a redirect
 * loop. Interactive deployments (and older servers that omit the capability)
 * retain the setup-token wizard. Terraform mode explicitly disables setup
 * tokens, so `/setup` leads to `/admin`, where ProtectedRoute presents the
 * normal OIDC sign-in path and provisioned users complete security onboarding.
 *
 * Status failures stay on the public wizard. This route must never infer that
 * setup-token authentication is disabled merely because the capability could
 * not be read.
 */
export const SetupRoute = (): JSX.Element | null => {
  const [destination, setDestination] = useState<SetupDestination>('loading')

  useEffect(() => {
    let cancelled = false
    createSetupClient()
      .getInitStatus()
      .then((resp) => {
        if (cancelled) return
        setDestination(
          resp.initialized || resp.setup_token_enabled === false
            ? 'admin'
            : 'wizard'
        )
      })
      .catch(() => {
        if (cancelled) return
        setDestination('wizard')
      })
    return () => {
      cancelled = true
    }
  }, [])

  if (destination === 'loading') return null
  if (destination === 'admin') return <Navigate to="/admin" replace />
  return <SetupWizard />
}
